import { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import Btn from '../shared/Btn';
import { fetchPreviewPdf } from '../../../api/claimsClient';

/**
 * @param {{ open: boolean, onClose: () => void, claimId: string|null }} props
 */
export default function CMS1500PreviewModal({ open, onClose, claimId }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(/** @type {string|null} */ (null));

  useEffect(() => {
    if (!open || !claimId) {
      setPreviewUrl('');
      setLoading(false);
      setLoadError(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';
    setPreviewUrl('');
    setLoading(true);
    setLoadError(null);

    fetchPreviewPdf(claimId)
      .then((pdfBlob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(pdfBlob);
        setPreviewUrl(objectUrl);
      })
      .catch((/** @type {any} */ err) => {
        if (cancelled) return;
        // Surface a real reason: 404 → claim not found; 500 → server failure
        // (often a missing-data edge case in the PDF builder); anything else
        // shows the network/HTTP message. Beats "Preview unavailable".
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail
          || err?.response?.statusText
          || err?.message
          || 'Unknown error';
        const reason = status === 404
          ? 'Claim not found.'
          : status === 500
          ? `Server failed to render the PDF: ${detail}`
          : status
          ? `${status}: ${detail}`
          : detail;
        setLoadError(reason);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, claimId]);

  const shouldShowLoading = loading || (open && claimId && !previewUrl && !loadError);

  return (
    <Modal
      open={open} onClose={onClose} title="CMS-1500 Preview" width={1100}
      footer={<>
        {previewUrl && (
          <a
            href={previewUrl}
            download={`claim-${claimId}-preview.pdf`}
            style={{ textDecoration: 'none' }}
          >
            <Btn kind="secondary" icon="download">Download PDF</Btn>
          </a>
        )}
        <Btn kind="primary" onClick={onClose}>Close</Btn>
      </>}
    >
      {shouldShowLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ab-fg-subtle)' }}>
          Loading preview...
        </div>
      ) : previewUrl ? (
        <iframe
          title="CMS-1500 Preview"
          src={previewUrl}
          style={{ width: '100%', height: '70vh', border: 'none' }}
        />
      ) : (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontWeight: 'var(--ab-weight-semi)', marginBottom: 6, color: 'var(--ab-denied)' }}>
            Preview unavailable
          </div>
          <div style={{ color: 'var(--ab-fg-subtle)', fontSize: 13 }}>
            {loadError || 'No preview to render.'}
          </div>
        </div>
      )}
    </Modal>
  );
}
